/**
 * Modelo de edição da prescrição de força (A3): converte entre os campos de texto do formulário
 * e o JSON da API. Lógica pura (sem React), testável com `node --test`.
 */
import type { ExerciseParam, StrengthItem, StrengthPrescription, StrengthSet } from "../api/types";

export interface EditSet {
  id: number;
  range: boolean; // repetições em faixa (mín–máx)
  reps: string;
  repsMin: string;
  repsMax: string;
  load: string;
  duration: string; // mm:ss ou minutos
  distance: string; // metros
  rest: string; // segundos
  tempo: string;
  note: string;
}

export interface EditItem {
  id: number;
  key?: string; // chave estável do item (liga resultados à prescrição)
  exerciseId: string;
  name: string;
  paramType: ExerciseParam;
  archived: boolean;
  note: string;
  sets: EditSet[];
}

export interface EditStrength {
  items: EditItem[];
  notes: string;
}

let seq = 1;
const nextId = () => seq++;

export function newSet(prev?: EditSet): EditSet {
  // repete a série anterior: o caso mais comum é 3 séries iguais
  return prev
    ? { ...prev, id: nextId() }
    : { id: nextId(), range: false, reps: "", repsMin: "", repsMax: "", load: "", duration: "", distance: "", rest: "", tempo: "", note: "" };
}

export function newItem(ex: { id: string; name: string; param_type: ExerciseParam }): EditItem {
  return { id: nextId(), exerciseId: ex.id, name: ex.name, paramType: ex.param_type, archived: false, note: "", sets: [newSet()] };
}

const secText = (s?: number) => (s === undefined ? "" : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`);
const numText = (n?: number) => (n === undefined ? "" : String(n).replace(".", ","));

export function toEditStrength(p: StrengthPrescription | null | undefined): EditStrength {
  return {
    notes: p?.notes ?? "",
    items: (p?.items ?? []).map((i) => ({
      id: nextId(),
      key: i.key,
      exerciseId: i.exercise_id,
      name: i.name ?? "",
      paramType: i.param_type ?? "reps_load",
      archived: false,
      note: i.note ?? "",
      sets: i.sets.map((s) => ({
        id: nextId(),
        range: s.reps_min !== undefined,
        reps: numText(s.reps),
        repsMin: numText(s.reps_min),
        repsMax: numText(s.reps_max),
        load: numText(s.load_kg),
        duration: secText(s.duration_s),
        distance: numText(s.distance_m),
        rest: numText(s.rest_s),
        tempo: s.tempo ?? "",
        note: s.note ?? "",
      })),
    })),
  };
}

const int = (txt: string): number | null => (/^\d{1,6}$/.test(txt.trim()) ? Number(txt.trim()) : null);

function duration(txt: string): number | null {
  const tp = txt.trim();
  if (!/^\d{1,3}(:\d{1,2}){0,2}$/.test(tp)) return null;
  const parts = tp.split(":").map(Number);
  if (parts.slice(1).some((p) => p >= 60)) return null;
  if (parts.length === 1) return parts[0] * 60;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] * 3600 + parts[1] * 60 + parts[2];
}

/** Erros de uma série: mapa campo → mensagem (chaves técnicas, o componente traduz). */
export type SetErrors = Partial<Record<"reps" | "load" | "duration" | "distance" | "rest" | "tempo", string>>;

export function setToJson(s: EditSet, param: ExerciseParam): { value: StrengthSet; errors: SetErrors } {
  const value: StrengthSet = {};
  const errors: SetErrors = {};
  const needsReps = param === "reps_load" || param === "reps";
  if (needsReps) {
    if (s.range) {
      const a = int(s.repsMin);
      const b = int(s.repsMax);
      if (a === null || b === null || a < 1 || b < 1 || a > 1000 || b > 1000 || a > b) errors.reps = "reps_range";
      else Object.assign(value, { reps_min: a, reps_max: b });
    } else {
      const r = int(s.reps);
      if (r === null || r < 1 || r > 1000) errors.reps = "reps";
      else value.reps = r;
    }
  }
  if (param === "reps_load" && s.load.trim()) {
    const n = Number(s.load.trim().replace(",", "."));
    if (!Number.isFinite(n) || n < 0 || n > 1000 || !/^\d+([.,]\d{1,2})?$/.test(s.load.trim())) errors.load = "load";
    else value.load_kg = n;
  }
  if (param === "time") {
    const d = duration(s.duration);
    if (d === null || d < 1 || d > 36000) errors.duration = "duration";
    else value.duration_s = d;
  }
  if (param === "distance") {
    const d = int(s.distance);
    if (d === null || d < 1 || d > 100000) errors.distance = "distance";
    else value.distance_m = d;
  }
  if (s.rest.trim()) {
    const r = int(s.rest);
    if (r === null || r > 3600) errors.rest = "rest";
    else value.rest_s = r;
  }
  if (s.tempo.trim()) {
    if (!/^[0-9X]-[0-9X]-[0-9X]-[0-9X]$/i.test(s.tempo.trim())) errors.tempo = "tempo";
    else value.tempo = s.tempo.trim().toUpperCase();
  }
  if (s.note.trim()) value.note = s.note.trim().slice(0, 200);
  return { value, errors };
}

export interface BuiltStrength {
  value: StrengthPrescription | null; // null = sem exercícios
  errors: Record<number, SetErrors>; // por id da série
  hasErrors: boolean;
}

export function buildStrength(e: EditStrength): BuiltStrength {
  const errors: Record<number, SetErrors> = {};
  const items: StrengthItem[] = e.items.map((i) => {
    const sets = i.sets.map((s) => {
      const r = setToJson(s, i.paramType);
      if (Object.keys(r.errors).length) errors[s.id] = r.errors;
      return r.value;
    });
    const item: StrengthItem = { exercise_id: i.exerciseId, sets };
    if (i.key) item.key = i.key;
    if (i.note.trim()) item.note = i.note.trim().slice(0, 300);
    return item;
  });
  const value: StrengthPrescription | null = items.length
    ? { items, ...(e.notes.trim() ? { notes: e.notes.trim().slice(0, 1000) } : {}) }
    : null;
  return { value, errors, hasErrors: Object.keys(errors).length > 0 };
}

/** Texto curto de uma série para leitura (ex.: "8 × 40 kg · 1:30 de descanso"). Sem tradução: recebe rótulos. */
export function describeSet(
  s: StrengthSet,
  labels: { kg: string; rest: string; reps: string },
): string {
  const parts: string[] = [];
  if (s.reps !== undefined) parts.push(`${s.reps} ${labels.reps}`);
  if (s.reps_min !== undefined) parts.push(`${s.reps_min}–${s.reps_max} ${labels.reps}`);
  if (s.load_kg !== undefined) parts.push(`${String(s.load_kg).replace(".", ",")} ${labels.kg}`);
  if (s.duration_s !== undefined) parts.push(secText(s.duration_s));
  if (s.distance_m !== undefined) parts.push(`${s.distance_m} m`);
  if (s.tempo) parts.push(s.tempo);
  if (s.rest_s !== undefined) parts.push(`${secText(s.rest_s)} ${labels.rest}`);
  return parts.join(" · ");
}
